<script setup lang="ts">

import Paginator from '@/onenetwork/components/paginator/Paginator.vue'
import { Icon } from '@iconify/vue'
import { computed, ref, toRaw, watch } from 'vue'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import Sort from '@/onenetwork/components/datatable/sort.vue'
import { uuid } from 'vue3-uuid'
import AllowPerm from '../perms/AllowPerm.vue'

const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  },
  buttons: Object,
  pageSize: {
    type: Number,
    default: 10
  },
  page: {
    type: Number,
    default: 1
  },
  totalPage: {
    type: Number,
    default: 0
  },
  totalRecords: {
    type: Number,
    default: 0
  },
  primaryField: {
    type: String,
    default: ''
  },
  displayConfig: {
    type: Boolean,
    default: true
  },
  data: Object,
  tableConfig: Object,
  rowActionButtons: Object,
  customActionCell: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['page-changed', 'pagesize-changed', 'toggle-visibility', 'on-row-click', 'on-row-dblclick', 'check-all', 'check-item', 'on-selected-row', 'on-sort'])

const displayConfig = computed(() => props.displayConfig)
const pageSize = ref(props.pageSize)
const visibilityPanelDisplay = ref(false)
const showHideButton = ref(null)
const totalPage = computed(() => {
  return props.totalPage
})
const _tableConfig = computed(() => {
  return props.tableConfig
})

const visibleColumns = computed(() => {
  const columns = Object.entries(props.tableConfig?.columns || {})
    .filter(([_, col]) => col.visible === true)
    .map(([key]) => key)

  if (props.rowActionButtons && props.rowActionButtons.length > 0) {
    columns.push('__actions')
  }

  return columns
})

const selectedRows = ref<any>([])

const checkAllRows = ref(false)

const handleToggleVisibility = (code: string, visible: boolean) => {
  emit('toggle-visibility', code, visible)
}

const handleToggleVisibilityPanelDisplay = () => {
  visibilityPanelDisplay.value = true
}

const handleClickOutsideVisibilityPanelDisplay = (e: MouseEvent) => {
  if(e.target as HTMLElement !== showHideButton.value) {

    visibilityPanelDisplay.value = false
  }
}

const handleRowClick = (e: MouseEvent, item: any) => {
  emit('on-row-click', e, item)
}
const handleRowDblclick = (e: MouseEvent, item: any) => {
  emit('on-row-dblclick', e, item)
}
const handleCheckAll = (e: MouseEvent) => {
  if(props.data?.length) {
    if(!checkAllRows.value){
      selectedRows.value = props.data.map((item: any) => {
        return item.id.toString()
      })
    }else{
      selectedRows.value = []
    }
  }
}
const handleCheckItem = (e: MouseEvent, item: any) => {
  let tmp = [...toRaw(selectedRows.value)]
  if(selectedRows.value.includes(item.id.toString())) {
    tmp = selectedRows.value.filter((id: string) => id !== item.id.toString())
  }else{
    tmp.push(item.id.toString())
  }
  selectedRows.value = tmp
  checkAllRows.value = tmp.length === props.data?.length

}

const handleSort = (field: string, direction: string) => {
  emit('on-sort', field, direction)
}

watch(pageSize, (newValue) => {
  emit('pagesize-changed', newValue)
})

watch(selectedRows, () => {

  checkAllRows.value = selectedRows.value.length === props.data?.length
  emit('on-selected-row', selectedRows.value)
})

watch(()=>props.data, ()=>{
  selectedRows.value = []
  checkAllRows.value = false
})

</script>

<template>

  <div :class="`table-responsive ${_tableConfig?.class}`">
    <div class="table-heading">
      <div class="flex justify-between">
      <div class="flex gap-2 items-center" v-if="displayConfig">
        <template v-if="data && data.length > 0">
          <div class="desc-display">Đang hiển thị <strong>{{data?.length}}</strong> trong tổng số <strong>{{totalRecords}}</strong> bản ghi</div>
          <div class="px-3">|</div>
        </template>
        <div class="config-display">
          <label>Tuỳ chọn</label>
          <select class="form-select text-right" aria-label="Default select example" v-model="pageSize">
            <option v-for="i in [1, 10, 20, 50, 100]" :key="`opt-pagesize-${i}`" :value="i" :selected="i === props.pageSize">{{i}} bản ghi trên trang</option>
          </select>
          <div class="config-columns" v-if="_tableConfig?.canShowHide">
            <button @click="handleToggleVisibilityPanelDisplay" ref="showHideButton">Ẩn / hiện cột</button>
            <div class="setting-columns-panel" v-if="visibilityPanelDisplay" v-click-outside="handleClickOutsideVisibilityPanelDisplay">
              <PerfectScrollbar :options="{
                suppressScrollX: false
              }">
                <div class="options">
                  <div
                    v-for="(col, index) in Object.keys(_tableConfig?.columns)"
                    :key="`optionItem${index}`"
                    :class="`option-item ${_tableConfig?.columns[col].visible ? 'active' : ''}`"
                    @click="()=>handleToggleVisibility(col, !_tableConfig?.columns[col].visible)"
                  >
                    <Icon
                      :icon="_tableConfig?.columns[col].visible ? 'akar-icons:check-box' : 'proicons:checkbox-unchecked'"
                      width="16" height="16"
                    />
                    <div class="label">{{ _tableConfig?.columns[col].label }}</div>
                  </div>
                </div>
              </PerfectScrollbar>
            </div>
          </div>
        </div>
      </div>

      <div class="text-right">
        <div v-if="props?.buttons?.length > 0" class="flex gap-2">
          <div v-for="(btn, idx) in props.buttons" :key="`dataTableActionBtn${idx}`">
            <AllowPerm :perm="btn.perm" action="hide">
              <button
                @click="btn.action"
                :disabled="btn.selectRequired && selectedRows?.length == 0"
                :class="`table-btn-action ${(btn.selectRequired && selectedRows?.length == 0) ? 'btn-disabled' : ''} ${btn.class}`">
                <Icon class="icon" :icon="btn.icon" v-if="btn.icon" height="18px"/>
                {{btn.label}}
              </button>
            </AllowPerm>
          </div>
          <!-- <button v-for="(btn, idx) in props.buttons" :key="`dataTableActionBtn${idx}`"
                  @click="btn.action"
                  :class="`table-btn-action ${btn.class}`">
            <Icon class="icon" :icon="btn.icon" v-if="btn.icon" height="18px"/>
            {{btn.label}}
          </button> -->
        </div>
      </div>
    </div>
    </div>
    <perfect-scrollbar :options="{ suppressScrollY:true }">
      <div class="table-container">
        <table class="table table-hover" v-if="_tableConfig">
          <thead>
            <tr>
              <template v-for="(col, idx) in Object.keys(_tableConfig.columns)" :key="`tableHeadCellKey${idx}`">
                <th v-if="_tableConfig.columns[col].visible" :class="`${_tableConfig.columns[col].class ? _tableConfig.columns[col].class : ''}`">
                  <template v-if="col === props.primaryField">
                    <div class="select-all" @click="handleCheckAll">
                      <Icon
                        :icon="checkAllRows ? 'ic:round-check-box' : 'proicons:checkbox-unchecked'"
                        width="16" height="16"
                      />
                      {{_tableConfig.columns[col].label}}
                    </div>
                  </template>
                  <template v-else>
                    <div class="flex gap-2 items-center" :class="`${_tableConfig.columns[col].class ? _tableConfig.columns[col].class : ''}`">
                      {{_tableConfig.columns[col].label}}
                      <sort :field="col" :dir="_tableConfig.columns[col].sort" @update:sort="handleSort"/>
                    </div>
                  </template>

                </th>
              </template>
              <AllowPerm :perm="props.rowActionButtons?.[0]?.perm" action="hide">
                <template v-if="!props.customActionCell && props.rowActionButtons && props.rowActionButtons.length > 0">
                  <th>Hành động</th>
                </template>
              </AllowPerm>
            </tr>
          </thead>
          <tbody v-if="data?.length > 0">
            <tr v-for="item in data" :key="`row-item-${item.id}`" :class="selectedRows.includes(item.id.toString()) ? 'selected': ''">
              <template v-for="(col, idx) in Object.keys(_tableConfig.columns)" :key="`tableBodyCellKey${idx}`">

                <td v-if="_tableConfig.columns[col].visible" @click="(e: MouseEvent)=>handleRowClick(e, item)" @dblclick="(e: MouseEvent) => handleRowDblclick(e, item)">
                  <template v-if="col === props.primaryField">
                    <div class="select-all">
                      <div class="icon"  @click="(e: MouseEvent)=>handleCheckItem(e, item)">
                      <Icon
                        :icon="selectedRows.includes(item.id.toString()) ? 'ic:round-check-box' : 'proicons:checkbox-unchecked'"
                        width="18" height="18"
                        :color="selectedRows.includes(item.id.toString()) ? '#007cf4' : '#dadada'"
                      />
                      </div>
                      <component :is="_tableConfig.columns[col].component" :data="item[col]"  :cell-config="_tableConfig.columns[col]"/>
                    </div>
                  </template>
                  <template v-else>
                    <component :is="_tableConfig.columns[col].component" :data="item[col]"  :cell-config="_tableConfig.columns[col]" :table-config="props"/>
                  </template>

                </td>
              </template>
              <AllowPerm :perm="props.rowActionButtons?.[0]?.perm" action="hide">
                <template v-if="!props.customActionCell && props.rowActionButtons && props.rowActionButtons.length > 0">
                  <td>
                    <template v-for="(btn, idx) in props.rowActionButtons" :key="'btn_action_'+idx+'_'+uuid.v4().toString()">
                      <button type="button" @click="(e: Event)=>btn.action(e, item)" class="flex items-center gap-1 whitespace-nowrap" :class="btn.css">
                        <template v-if="btn.icon"><Icon :icon="btn.icon"/></template>
                        {{btn.label}}
                      </button>
                    </template>
                  </td>
                </template>
              </AllowPerm>
            </tr>
          </tbody>
          <tbody v-if="!loading && data?.length == 0">
            <tr>
              <td :colspan="visibleColumns.length">
                  <div class="flex items-center gap-3 justify-center p-4 text-[#c9c9c9]">
                    <Icon icon="f7:cloud-sun-rain" width="24" height="24" />  Không tìm thấy dữ liệu
                  </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="table-loading" v-if="loading">
          <span class="loading">
            <Icon icon="svg-spinners:blocks-shuffle-3" />
          </span>
          <span>Đang tải...</span>
        </div>
      </div>
    </perfect-scrollbar>
    <paginator
      v-if="totalPage > 1"
      :page-size="pageSize"
      :total="totalRecords"
      :sibling-count="2"
      :default-page="1"
      @page-changed="(page: number) => emit('page-changed', page)"
    />
  </div>
</template>

<style lang="scss">
.config-display {
  font-size: .75rem;
  font-weight: 200;
  color: gray;
}
.desc-display{
  font-size: .75rem;
  font-weight: 300;
  strong{
    font-weight: bold;
    margin-left: 5px;
    margin-right: 5px;
  }
}
</style>
